import React, { useEffect, useState } from "react";
import {
  Check,
  Car,
  Users,
  MapPin,
  Route,
  Clock,
  Fuel,
  Shield,
  Star,
  Navigation,
} from "lucide-react";

function SignupSuccess({ email, name }) {
  const [countdown, setCountdown] = useState(20);
  const [isVisible, setIsVisible] = useState(false);
  const [carPosition, setCarPosition] = useState(0);
  const [roadElements, setRoadElements] = useState([]);

  useEffect(() => {
    // Entrance animation
    setTimeout(() => setIsVisible(true), 300);

    // Generate road elements (dashes)
    const elements = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      x: (i * 6) % 100,
      delay: i * 0.1,
    }));
    setRoadElements(elements);

    // Car animation
    const carInterval = setInterval(() => {
      setCarPosition((prev) => (prev + 1) % 100);
    }, 100);

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.location.href = "/login";
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      clearInterval(carInterval);
    };
  }, []);

  const progressPercentage = ((20 - countdown) / 20) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
      {/* Animated Road Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Sky gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-100/30 via-transparent to-green-100/20"></div>

        {/* Clouds */}
        <div className="absolute top-5 left-5 w-16 h-10 bg-white/60 rounded-full blur-sm animate-pulse"></div>
        <div className="absolute top-10 right-16 w-14 h-6 bg-white/50 rounded-full blur-sm animate-pulse animation-delay-1000"></div>
        <div className="absolute top-6 left-1/3 w-20 h-8 bg-white/40 rounded-full blur-sm animate-pulse animation-delay-2000"></div>

        {/* Road */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-700 via-gray-600 to-transparent transform perspective-1000 rotate-x-12">
          {/* Road markings */}
          {roadElements.map((element) => (
            <div
              key={element.id}
              className="absolute bottom-12 w-10 h-1.5 bg-yellow-300 animate-pulse opacity-80"
              style={{
                left: `${element.x}%`,
                animationDelay: `${element.delay}s`,
              }}
            />
          ))}
        </div>

        {/* Moving car */}
        <div
          className="absolute bottom-16 transition-all duration-100 ease-linear"
          style={{ left: `${carPosition}%` }}
        >
          <Car className="w-6 h-6 text-blue-600 animate-bounce" />
        </div>

        {/* Trees and scenery */}
        <div className="absolute bottom-24 left-4 w-3 h-6 bg-green-600 rounded-t-full animate-sway"></div>
        <div className="absolute bottom-24 right-8 w-3 h-6 bg-green-700 rounded-t-full animate-sway animation-delay-500"></div>
        <div className="absolute bottom-24 left-1/4 w-2.5 h-5 bg-green-500 rounded-t-full animate-sway animation-delay-1000"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div
          className={`transform transition-all duration-1000 ease-out ${
            isVisible
              ? "translate-y-0 opacity-100 scale-100"
              : "translate-y-8 opacity-0 scale-95"
          } w-full max-w-md`}
        >
          {/* Main Card */}
          <div className="relative bg-white/90 backdrop-blur-xl rounded-2xl p-6 max-w-md w-full shadow-2xl shadow-blue-500/10 border border-white/50">
            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center animate-bounce">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div className="absolute -top-3 -right-3 w-7 h-7 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center animate-pulse">
              <Users className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="absolute -bottom-4 -right-4 w-9 h-9 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center animate-bounce animation-delay-1000">
              <Route className="w-4 h-4 text-white" />
            </div>

            {/* Success Icon with Car Theme */}
            <div className="relative mx-auto w-20 h-20 mb-5">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full animate-spin-slow opacity-20"></div>
              <div className="absolute inset-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                <Check
                  className="w-9 h-9 text-white animate-bounce"
                  strokeWidth={3}
                />
              </div>

              {/* Floating cars around icon */}
              <div className="absolute -inset-10">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute animate-ping opacity-60"
                    style={{
                      top: `${30 + Math.sin((i * Math.PI) / 3) * 25}%`,
                      left: `${30 + Math.cos((i * Math.PI) / 3) * 25}%`,
                      animationDelay: `${i * 0.3}s`,
                    }}
                  >
                    <Car className="w-3 h-3 text-blue-500" />
                  </div>
                ))}
              </div>
            </div>

            {/* Welcome Message */}
            <div className="text-center mb-5">
              <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 mb-2">
                🚗 Ready to Ride!
              </h1>
              <p className="text-gray-600 text-sm mb-1">
                Your carpooling journey starts here
              </p>
              <p className="text-lg font-semibold text-blue-600 flex items-center justify-center gap-1">
                <Navigation className="w-4 h-4 text-green-500 animate-pulse" />
                Welcome, {name}!
                <Star className="w-4 h-4 text-yellow-500 animate-pulse" />
              </p>
            </div>

            {/* Feature Cards */}
            <div className="space-y-2 mb-5">
              <div className="group bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 hover:border-blue-300 transition-all duration-300 hover:shadow-md hover:shadow-blue-200/50">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md group-hover:rotate-12 transition-transform duration-300">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-blue-800 text-sm mb-0.5">
                      Account Verified
                    </h3>
                    <p className="text-blue-600 text-xs truncate">
                      {email} confirmed
                    </p>
                  </div>
                  <MapPin className="w-4 h-4 text-red-500 animate-pulse" />
                </div>
              </div>

              <div className="group bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 hover:border-green-300 transition-all duration-300 hover:shadow-md hover:shadow-green-200/50">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md group-hover:rotate-12 transition-transform duration-300">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-green-800 text-sm mb-0.5">
                      Find Your Ride
                    </h3>
                    <p className="text-green-600 text-xs">
                      Connect with drivers and passengers! 🌍
                    </p>
                  </div>
                  <Fuel className="w-4 h-4 text-orange-500 animate-pulse animation-delay-500" />
                </div>
              </div>

              <div className="group bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-3 hover:border-purple-300 transition-all duration-300 hover:shadow-md hover:shadow-purple-200/50">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-md group-hover:rotate-12 transition-transform duration-300">
                    <Route className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-purple-800 text-sm mb-0.5">
                      Smart Routes
                    </h3>
                    <p className="text-purple-600 text-xs">
                      Optimized paths to save time and money! 💰
                    </p>
                  </div>
                  <Clock className="w-4 h-4 text-blue-500 animate-pulse animation-delay-1000" />
                </div>
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={() => (window.location.href = "/login")}
              className="group relative w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 hover:from-blue-600 hover:via-indigo-600 hover:to-purple-600 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/30 overflow-hidden mb-4"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 group-hover:animate-pulse"></div>
              <div className="relative flex items-center justify-center gap-2">
                <Car className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                <span className="text-sm">Start Carpooling</span>
                <Navigation className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
              </div>
            </button>

            {/* Progress Bar */}
            <div className="relative mb-3">
              <div className="w-full bg-blue-100 rounded-full h-2.5 overflow-hidden border border-blue-200">
                <div
                  className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 h-full rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
                  style={{ width: `${progressPercentage}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-pulse"></div>
                </div>
              </div>
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 via-indigo-500/20 to-purple-500/20 rounded-full blur-sm -z-10"></div>
            </div>

            {/* Countdown */}
            <div className="text-center">
              <p className="text-blue-600 text-xs font-medium flex items-center justify-center gap-1">
                <Car className="w-3.5 h-3.5 animate-bounce text-blue-500" />
                <span>Taking you to login in {countdown} seconds...</span>
                <Route className="w-3.5 h-3.5 animate-bounce animation-delay-500 text-purple-500" />
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes sway {
          0%,
          100% {
            transform: rotate(-2deg);
          }
          50% {
            transform: rotate(2deg);
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        .animate-sway {
          animation: sway 4s ease-in-out infinite;
        }
        .animation-delay-500 {
          animation-delay: 0.5s;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .perspective-1000 {
          perspective: 1000px;
        }
        .rotate-x-12 {
          transform: rotateX(12deg);
        }
      `}</style>
    </div>
  );
}

export default SignupSuccess;
